import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn.js';

// ---------------------------------------------------------------------------
// Modal
//
// Lightweight modal primitive. Two consumers today: the studio invite modal
// and the Discord disconnect confirm dialog. Both want the same behaviour:
// backdrop click to dismiss, ESC to dismiss, an aria-labelledby anchor on
// the title, and a `busy` flag that suppresses dismiss attempts while a
// submission is in-flight.
//
// Body content is opinion-free: callers compose with Card.Header /
// Card.Content / Card.Footer or hand-roll their own layout. The primitive
// only owns the overlay + dismiss + focus-trap-light behaviour.
// ---------------------------------------------------------------------------

export type ModalProps = {
  /** Whether the modal is visible. */
  open: boolean;
  /** Called when the user dismisses via backdrop click or ESC. Suppressed while busy. */
  onClose: () => void;
  /** ID of the element that labels the dialog. Hooked into aria-labelledby. */
  labelledById: string;
  /**
   * Disables backdrop-click and ESC dismissal. Use while a submit is in
   * flight so the user cannot orphan a request.
   */
  busy?: boolean;
  /** Modal max-width. Defaults to md. */
  size?: 'sm' | 'md' | 'lg';
  /** Optional ref pointing at the element that should receive focus on open. */
  initialFocusRef?: React.RefObject<HTMLElement>;
  className?: string;
  children: ReactNode;
};

const sizeClass: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

export function Modal({
  open,
  onClose,
  labelledById,
  busy = false,
  size = 'md',
  initialFocusRef,
  className,
  children,
}: ModalProps): JSX.Element | null {
  // Keep the modal in the DOM while the closing transition runs. Without
  // this, an `opacity-0` exit animation gets cut off the moment React
  // unmounts the tree.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = window.requestAnimationFrame(() => {
        initialFocusRef?.current?.focus();
      });
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [open, initialFocusRef]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!mounted && !open) return null;

  // Portal to document.body so the modal escapes any ancestor that
  // would otherwise inherit through to it (opacity, transforms, overflow
  // clips). Critical for cards that get an opacity mute (dismissed
  // Insights cards) - the modal must read at full opacity even when its
  // mounting parent is half-faded.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledById}
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/50 transition-opacity',
        open ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className={cn('w-full mx-4', sizeClass[size], className)}>{children}</div>
    </div>,
    document.body,
  );
}

// Re-export a small convenience hook so consumers don't have to wire up
// useRef themselves for the auto-focus pattern (cancel button focused so
// Enter doesn't accidentally trigger destructive primary actions).
export function useInitialFocus<T extends HTMLElement>(): React.RefObject<T> {
  return useRef<T>(null);
}
